import React, { useEffect } from 'react';
import { Modal, Form, Input, Button, DatePicker, Upload, Radio } from 'antd';
import LocationSelect from "../../../components/LocationSelect";
import DoctorsSelect from "./DoctorsList";
import moment from 'moment';
import { BiInfoCircle } from "react-icons/bi";
import InputMask from 'react-input-mask';
import { useTranslation } from 'react-i18next';

const PatientFormModal = ({ visible, onClose, onSubmit, loading, initialValues }) => {
    const { t } = useTranslation();
    const [form] = Form.useForm();

    useEffect(() => {
        if (initialValues) {
            form.setFieldsValue({
                ...initialValues,
                location: {
                    province_id: initialValues.province_id,
                    district_id: initialValues.district_id,
                },
                birth_at: initialValues.birth_at ? moment(initialValues.birth_at) : null,
            });
        } else {
            form.resetFields();
        }
    }, [initialValues, form]);

    useEffect(() => {
        if (!visible) {
            form.resetFields();
        }
    }, [visible, form]);

    const handleFinish = (values) => {
        onSubmit(values);
    };

    return (
        <Modal
            centered
            width="100%"
            style={{maxWidth: 1000, top: 8, paddingBottom: 8}}
            visible={visible}
            title={initialValues ? t('patients.formEdit', { name: initialValues.name }) : t('patients.formCreate')}
            onCancel={onClose}
            footer={null}
        >
            <Form
                form={form}
                layout="vertical"
                onFinish={handleFinish}
            >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-3 gap-y-0">
                    <Form.Item
                        name="name"
                        label={t('patients.formName')}
                        rules={[{ required: true, message: t('patients.formNameReq') }]}
                    >
                        <Input placeholder={t('patients.formName')} className="h-10" />
                    </Form.Item>
                    <Form.Item
                        className="sm:col-span-2"
                        label={t('patients.formAddress')}
                        name="location"
                        rules={[{ required: true, message: t('patients.formAddressReq') }]}
                    >
                        <LocationSelect />
                    </Form.Item>
                    <Form.Item
                        name="partner_id"
                        label={t('patients.formPartner')}
                        rules={[{ required: true, message: t('patients.formPartnerReq') }]}
                    >
                        <DoctorsSelect />
                    </Form.Item>
                    <Form.Item
                        tooltip={{
                            title: t('patients.formPhoneHint'),
                            icon: <BiInfoCircle />,
                        }}
                        name="phone"
                        label={t('patients.formPhone')}
                        rules={[
                            { required: true, message: t('patients.formPhoneReq') }
                        ]}
                    >
                        <InputMask
                            placeholder="+998 (__) ___-__-__"
                            mask="+\9\9\8 (99) 999-99-99"
                            maskChar="_"
                            minLength="12"
                            className="h-10"
                            onChange={(e) => {
                                const { value } = e.target;
                                const formattedValue = value.replace(/[^\d+]/g, '');
                                e.target.value = formattedValue;
                            }}
                        >
                            {(inputProps) => <Input prefix={<img src="/icons8-узбекистан-48.png" alt="" className="w-6 h-6"/>} {...inputProps} />}
                        </InputMask>
                    </Form.Item>
                    <Form.Item
                        name="profession"
                        label={t('patients.formProfession')}
                    >
                        <Input placeholder={t('patients.formProfession')} className="h-10" />
                    </Form.Item>

                    <Form.Item
                        name="birth_at"
                        label={t('patients.formBirth')}
                        rules={[{ required: true, message: t('patients.formBirthReq') }]}
                    >
                        <DatePicker format="DD-MM-YYYY" className="w-full h-10" />
                    </Form.Item>
                    <Form.Item
                        name="home_address"
                        label={t('patients.formHome')}
                        rules={[{ required: true, message: t('patients.formHomeReq') }]}
                    >
                        <Input placeholder={t('patients.formHome')} className="h-10" />
                    </Form.Item>
                    <Form.Item
                        name="work_address"
                        label={t('patients.formWork')}
                    >
                        <Input placeholder={t('patients.formWork')} className="h-10" />
                    </Form.Item>
                    <Form.Item
                        name="remark"
                        label={t('patients.formRemark')}
                    >
                        <Input placeholder={t('patients.formRemark')} className="h-10" />
                    </Form.Item>
                    <Form.Item
                        label={t('patients.formGender')}
                        name="gender"
                        className="w-full"
                        rules={[{ required: true, message: t('patients.formGenderReq') }]}
                    >
                        <Radio.Group
                            size="middle"
                            optionType="button"
                            className="w-full items-center"
                            style={{ display: 'flex', justifyContent: 'space-between' }}
                        >
                            <Radio.Button className="h-10 flex items-center justify-center" value="men" style={{ flex: 1, textAlign: 'center' }}>
                                {t('patientCard.male')}
                            </Radio.Button>
                            <Radio.Button className="h-10 flex items-center justify-center" value="women" style={{ flex: 1, textAlign: 'center' }}>
                                {t('patientCard.female')}
                            </Radio.Button>
                        </Radio.Group>
                    </Form.Item>

                    <Form.Item
                        name="file"
                        label={t('patients.formPhoto')}
                        className="w-full"
                    >
                        <Upload.Dragger
                            className="w-full"
                            name="file"
                            multiple={false}
                            beforeUpload={() => false}
                            showUploadList={false}
                        >
                            <p className="ant-upload-drag-icon">
                                <svg  xmlns="http://www.w3.org/2000/svg" width="35" height="35" viewBox="0 0 24 24"
                                     fill="none" stroke="#1D2C7B" stroke-width="1.6" stroke-linecap="round"
                                     stroke-linejoin="round" className="lucide lucide-image-up mx-auto">
                                    <path
                                        d="M10.3 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10l-3.1-3.1a2 2 0 0 0-2.814.014L6 21"/>
                                    <path d="m14 19.5 3-3 3 3"/>
                                    <path d="M17 22v-5.5"/>
                                    <circle cx="9" cy="9" r="2"/>
                                </svg>
                            </p>
                            <p className="ant-upload-text">{t('patients.formUpload')}</p>
                            <p className="ant-upload-hint">
                                {t('patients.formUploadHint')}
                            </p>
                        </Upload.Dragger>
                    </Form.Item>
                </div>
                <Form.Item>
                    <Button className="w-full" type="primary" htmlType="submit" loading={loading}>
                        {initialValues ? t('patients.formSubmit') : t('patients.formSubmitAdd')}
                    </Button>
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default PatientFormModal;
